// ─────────────────────────────────────────────
//  native/BiometricGate.jsx — biometric app-lock overlay (Phase 6, D-09)
//
//  Wraps the whole app. On WEB it renders children immediately (zero cost).
//  On NATIVE, if the user opted into "require biometric unlock" (appLock
//  preference) and the device can do biometrics, a lock screen covers the
//  UI on cold start until the native biometric sheet succeeds. Cancel keeps
//  the lock screen up with a retry button (WCAG-visible, keyboardable).
//
//  Graceful fallback: opt-out, no enrolled biometrics, plugin failure, or
//  web platform → children render normally. Firebase Auth session is never
//  touched — this is a view gate, not an auth layer.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
// ─────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from "react";
import tokens from "../theme/tokens";
import Button from "../components/Button";
import { isNative } from "./platform";
import { getAppLockEnabled } from "./appLock";
import { biometricsAvailable, biometricAuthenticate } from "./biometrics";

const { color, space, font } = tokens;

export default function BiometricGate({ children }) {
  // Web renders children synchronously — no flicker, no async gate.
  const [locked, setLocked] = useState(false);
  const [busy, setBusy] = useState(false);

  const tryUnlock = useCallback(async () => {
    setBusy(true);
    const ok = await biometricAuthenticate();
    setBusy(false);
    if (ok) setLocked(false);
  }, []);

  useEffect(() => {
    if (!isNative()) return undefined;
    let cancelled = false;
    (async () => {
      const enabled = await getAppLockEnabled();
      const canBio = enabled && (await biometricsAvailable());
      if (cancelled || !canBio) return;
      setLocked(true);
      tryUnlock(); // fire the sheet immediately on cold start
    })();
    return () => {
      cancelled = true;
    };
  }, [tryUnlock]);

  if (!locked) return children;

  return (
    <main style={styles.wrap} aria-label="App locked">
      <h1 style={styles.title}>Anesthesia Team Control</h1>
      <p style={styles.sub}>Unlock with your fingerprint or face to continue.</p>
      <Button variant="primary" size="md" onClick={tryUnlock} loading={busy}>
        Unlock
      </Button>
    </main>
  );
}

const styles = {
  wrap: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: space.lg,
    background: color.surface.base,
    padding: space.xl,
    textAlign: "center",
  },
  title: {
    margin: 0,
    color: color.text.primary,
    fontSize: font.size.display,
    fontWeight: font.weight.bold,
  },
  sub: {
    margin: 0,
    color: color.text.secondary,
    fontSize: font.size.body,
  },
};
